import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, AlertCircle, Clock, Lock, ExternalLink, Maximize2, Image as ImageIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { AxiosError } from 'axios';
import api from '@/services/api';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import ReceiptImageViewer from '@/components/receipts/ReceiptImageViewer';

interface SharedReceiptData {
  receipt: {
    originalFilename: string;
    mimeType: string;
    merchant?: string;
    total?: number | null;
    currency?: string;
    items?: { name: string; quantity?: number; price?: number }[];
    notes?: string;
  };
  share: {
    expiresAt: string;
    createdAt: string;
  };
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function normalizeItems(value: unknown): { name: string; quantity?: number; price?: number }[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter(isRecord)
    .map((item) => {
      const quantity = asNumberOrNull(item.quantity);
      const price = asNumberOrNull(item.price);

      return {
        name: asString(item.name, 'Item'),
        ...(quantity != null ? { quantity } : {}),
        ...(price != null ? { price } : {}),
      };
    });
}

function normalizeSharedReceiptPayload(payload: unknown): SharedReceiptData | null {
  if (!isRecord(payload)) {
    return null;
  }

  const nestedReceipt = isRecord(payload.receipt) ? payload.receipt : null;
  const nestedShare = isRecord(payload.share) ? payload.share : null;
  const receiptSource = nestedReceipt ?? payload;
  const shareSource = nestedShare ?? payload;

  const originalFilename = asString(
    receiptSource.originalFilename,
    asString(receiptSource.filename, asString(receiptSource.fileName, 'receipt')),
  );

  const mimeType = asString(
    receiptSource.mimeType,
    asString(receiptSource.contentType, 'application/octet-stream'),
  );

  const total = asNumberOrNull(receiptSource.total) ?? asNumberOrNull(receiptSource.amount);
  const merchant = asString(receiptSource.merchant);
  const currency = asString(receiptSource.currency);
  const notes = asString(receiptSource.notes);
  const items = normalizeItems(receiptSource.items);

  const createdAt = asString(shareSource.createdAt);
  const expiresAt = asString(shareSource.expiresAt);

  const hasReceiptSignal =
    nestedReceipt !== null ||
    originalFilename !== 'receipt' ||
    mimeType !== 'application/octet-stream' ||
    total != null ||
    merchant.length > 0 ||
    items.length > 0;

  const hasShareSignal = nestedShare !== null || createdAt.length > 0 || expiresAt.length > 0;

  if (!hasReceiptSignal && !hasShareSignal) {
    return null;
  }

  return {
    receipt: {
      originalFilename,
      mimeType,
      ...(merchant ? { merchant } : {}),
      total,
      ...(currency ? { currency } : {}),
      ...(items.length > 0 ? { items } : {}),
      ...(notes ? { notes } : {}),
    },
    share: {
      createdAt,
      expiresAt,
    },
  };
}

function formatShareDate(value: string): string {
  if (!value) {
    return 'Unknown date';
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Unknown date' : parsed.toLocaleDateString();
}

export default function SharedReceipt() {
  useDocumentTitle('Shared Receipt');
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<SharedReceiptData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerSrc, setViewerSrc] = useState('');
  const [blobFallbackAttempted, setBlobFallbackAttempted] = useState(false);
  const objectUrlRef = useRef<string | null>(null);
  const fallbackAttemptIdRef = useRef(0);

  const baseUrl = api.defaults.baseURL || '';
  const fileUrl = token ? `${baseUrl}/shared/${token}/file` : '';

  const releaseObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const attemptBlobFallback = useCallback(async () => {
    if (!fileUrl || blobFallbackAttempted) {
      setImageLoading(false);
      setImageError(true);
      return;
    }

    setBlobFallbackAttempted(true);
    const attemptId = ++fallbackAttemptIdRef.current;

    try {
      const response = await fetch(fileUrl, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'omit',
      });

      if (!response.ok) {
        throw new Error(`Fallback fetch failed with status ${response.status}`);
      }

      const blob = await response.blob();
      if (!blob.size) {
        throw new Error('Fallback blob is empty');
      }

      const objectUrl = URL.createObjectURL(blob);

      if (attemptId !== fallbackAttemptIdRef.current) {
        URL.revokeObjectURL(objectUrl);
        return;
      }

      releaseObjectUrl();
      objectUrlRef.current = objectUrl;
      setViewerSrc(objectUrl);
      setImageError(false);
      setImageLoading(true);
    } catch {
      if (attemptId !== fallbackAttemptIdRef.current) {
        return;
      }
      setImageLoading(false);
      setImageError(true);
    }
  }, [blobFallbackAttempted, fileUrl, releaseObjectUrl]);

  useEffect(() => {
    if (!token) {
      setError('Invalid share link');
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const res = await api.get(`/shared/${token}`);
        const normalized = normalizeSharedReceiptPayload(res.data?.data ?? res.data);
        if (!normalized) {
          setError('This shared receipt payload is invalid or incomplete.');
        } else {
          setData(normalized);
        }
      } catch (err: unknown) {
        const status = (err as AxiosError)?.response?.status;
        if (status === 404) {
          setError('This share link is invalid or has been deleted.');
        } else if (status === 410) {
          setError('This share link has expired.');
        } else if (status === 429) {
          setError('This share link has reached its maximum number of accesses.');
        } else {
          setError('Failed to load shared receipt. Please try again.');
        }
      }
      setLoading(false);
    })();
  }, [token]);

  useEffect(() => {
    fallbackAttemptIdRef.current += 1;
    releaseObjectUrl();
    setViewerSrc(fileUrl);
    setImageLoading(true);
    setImageError(false);
    setViewerOpen(false);
    setBlobFallbackAttempted(false);
  }, [fileUrl, releaseObjectUrl]);

  useEffect(() => {
    return () => {
      releaseObjectUrl();
    };
  }, [releaseObjectUrl]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
          <p className="mt-4 text-sm text-muted-foreground">Loading shared receipt...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-4 text-center" data-testid="shared-receipt-error">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <h1 className="text-xl font-bold">Unable to view receipt</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <p className="text-xs text-muted-foreground">Shared via FinTrack</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-4 text-center" data-testid="shared-receipt-error">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <h1 className="text-xl font-bold">Unable to view receipt</h1>
          <p className="text-sm text-muted-foreground">
            This shared receipt payload is invalid or incomplete.
          </p>
          <p className="text-xs text-muted-foreground">Shared via FinTrack</p>
        </div>
      </div>
    );
  }

  const { receipt, share } = data;
  const mimeType = receipt.mimeType || 'application/octet-stream';
  const filename = receipt.originalFilename || 'receipt';
  const isPdf = mimeType.toLowerCase() === 'application/pdf';

  return (
    <div className="min-h-screen bg-background" data-testid="shared-receipt-page">
      {/* Header */}
      <header className="border-b bg-card px-4 py-3">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded bg-primary">
              <span className="text-xs font-bold text-primary-foreground">F</span>
            </div>
            <span className="text-sm font-semibold">FinTrack</span>
          </div>
          <Badge variant="outline" className="text-xs">
            <Lock className="mr-1 h-3 w-3" /> Shared receipt
          </Badge>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-3xl space-y-6 p-4 md:p-8">
        {/* Receipt Image */}
        <div className="overflow-hidden rounded-lg border bg-card">
          {isPdf ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <p className="text-sm text-muted-foreground">PDF Receipt</p>
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="shared-receipt-open-pdf"
                className="mt-2 text-sm font-medium text-primary underline"
              >
                Open PDF
              </a>
            </div>
          ) : (
            <div className="relative h-[60vh] min-h-[320px]">
              {!imageError && (
                <ReceiptImageViewer
                  src={viewerSrc}
                  alt={filename}
                  mimeType={mimeType}
                  className="h-full"
                  imageTestId="shared-receipt-image"
                  zoomInTestId="shared-receipt-zoom-in"
                  zoomOutTestId="shared-receipt-zoom-out"
                  zoomResetTestId="shared-receipt-zoom-reset"
                  onImageLoad={() => {
                    setImageLoading(false);
                    setImageError(false);
                  }}
                  onImageError={() => {
                    if (!blobFallbackAttempted && viewerSrc === fileUrl) {
                      void attemptBlobFallback();
                      return;
                    }

                    setImageLoading(false);
                    setImageError(true);
                  }}
                />
              )}

              {imageLoading && !imageError && (
                <div
                  className="absolute inset-0 flex items-center justify-center bg-muted/20"
                  data-testid="shared-receipt-image-loading"
                >
                  <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
                </div>
              )}

              {imageError && (
                <div
                  className="absolute inset-0 flex items-center justify-center p-6"
                  data-testid="shared-receipt-image-error"
                >
                  <div className="max-w-sm text-center">
                    <ImageIcon className="mx-auto h-8 w-8 text-muted-foreground" />
                    <p className="mt-3 text-sm text-muted-foreground">
                      We could not decode this image preview in your browser.
                    </p>
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="shared-receipt-image-open-original"
                      className="mt-3 inline-flex items-center text-sm font-medium text-primary underline"
                    >
                      Open original file
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {!isPdf && (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-[44px]"
              onClick={() => setViewerOpen(true)}
              disabled={imageLoading || imageError}
              data-testid="shared-receipt-open-fullsize"
            >
              <Maximize2 className="mr-2 h-4 w-4" />
              View Full Size
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="min-h-[44px]"
              asChild
              data-testid="shared-receipt-open-original"
            >
              <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                Open Original
              </a>
            </Button>
          </div>
        )}

        {/* Metadata */}
        <div className="space-y-4 rounded-lg border bg-card p-4 md:p-6">
          {receipt.merchant && (
            <div>
              <p className="text-xs text-muted-foreground">Merchant</p>
              <p className="text-lg font-semibold break-words">{receipt.merchant}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            {receipt.total != null && (
              <div>
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="text-sm font-bold">
                  {receipt.currency ? `${receipt.currency} ` : ''}
                  {receipt.total.toFixed(2)}
                </p>
              </div>
            )}
          </div>

          {receipt.items && receipt.items.length > 0 && (
            <div>
              <p className="mb-2 text-xs text-muted-foreground">Items</p>
              <div className="space-y-1">
                {receipt.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="break-words">
                      {item.name}
                      {item.quantity && item.quantity > 1 ? ` × ${item.quantity}` : ''}
                    </span>
                    {item.price != null && (
                      <span className="ml-2 font-medium">{item.price.toFixed(2)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {receipt.notes && (
            <div>
              <p className="text-xs text-muted-foreground">Notes</p>
              <p className="text-sm break-words">{receipt.notes}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Shared on {formatShareDate(share.createdAt)}
          </div>
          <span>Shared via FinTrack</span>
        </div>
      </main>

      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent
          className="h-[90vh] w-[95vw] max-w-5xl overflow-hidden p-0"
          data-testid="shared-receipt-fullsize-dialog"
        >
          <DialogHeader className="border-b px-4 py-3">
            <DialogTitle className="truncate text-base">{filename}</DialogTitle>
            <DialogDescription className="sr-only">
              Full-size shared receipt image viewer with zoom controls.
            </DialogDescription>
          </DialogHeader>
          <div className="h-[calc(90vh-64px)] p-3">
            <ReceiptImageViewer
              src={viewerSrc}
              alt={filename}
              mimeType={mimeType}
              className="h-full"
              imageTestId="shared-receipt-fullsize-image"
              zoomInTestId="shared-receipt-fullsize-zoom-in"
              zoomOutTestId="shared-receipt-fullsize-zoom-out"
              zoomResetTestId="shared-receipt-fullsize-zoom-reset"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
